import React, { createContext, useContext, useState } from 'react';
import { FrameStyle, ImageStyle } from '../types';
import { getOrCreateClientSessionId } from '../services/imageProcessing';

interface FrameContextType {
  frameStyle: FrameStyle;
  imageStyle: ImageStyle;
  isOrientaAddress: boolean;
  isChapelHouse: boolean;
  selectedStyle: string;
  address: string;
  // Structured address fields for dynamic title
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  selectedFrame: string;
  carouselImages: string[];
  clientSessionId: string;
  selectedSize: string;
  propertyImage: string | null;
  styleImages: Record<ImageStyle, string | null>;
  propertySearchId: string | null;
  imageSource: 'address' | 'upload' | 'webcam' | null;
  setFrameStyle: (style: FrameStyle) => void;
  setImageStyle: (style: ImageStyle) => void;
  setIsOrientaAddress: (isOrienta: boolean) => void;
  setIsChapelHouse: (isChapel: boolean) => void;
  setAddress: (address: string) => void;
  // Setters for structured address fields
  setAddressLine1: (addressLine1: string | null) => void;
  setCity: (city: string | null) => void;
  setState: (state: string | null) => void;
  setZipCode: (zipCode: string | null) => void;
  setSelectedStyle: (style: string) => void;
  setSelectedFrame: (frame: string) => void;
  setCarouselImages: (images: string[]) => void;
  setClientSessionId: (id: string) => void;
  setSelectedSize: (size: string) => void;
  setPropertyImage: (image: string | null) => void;
  setStyleImages: (images: Record<ImageStyle, string | null>) => void;
  setPropertySearchId: (id: string | null) => void;
  setImageSource: (source: 'address' | 'upload' | 'webcam' | null) => void;
}

const FrameContext = createContext<FrameContextType | null>(null);

export const useFrame = () => {
  const context = useContext(FrameContext);
  if (!context) {
    throw new Error('useFrame must be used within a FrameProvider');
  }
  return context;
};

export const FrameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [frameStyle, setFrameStyle] = useState<FrameStyle>('wood');
  const [imageStyle, setImageStyle] = useState<ImageStyle>('pencil');
  const [isOrientaAddress, setIsOrientaAddress] = useState(false);
  const [isChapelHouse, setIsChapelHouse] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('watercolor');
  const [address, setAddress] = useState('');
  // Structured address state
  const [addressLine1, setAddressLine1] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [zipCode, setZipCode] = useState<string | null>(null);
  const [selectedFrame, setSelectedFrame] = useState('black');
  const [carouselImages, setCarouselImages] = useState<string[]>([]);
  const [clientSessionId, setClientSessionId] = useState(getOrCreateClientSessionId());
  const [selectedSize, setSelectedSize] = useState('S'); // 'S' displays as "M" to users
  const [propertyImage, setPropertyImage] = useState<string | null>(null);
  const [styleImages, setStyleImages] = useState<Record<ImageStyle, string | null>>({
    pencil: null,
    oil: null,
    charcoal: null,
    chalk: null,
  });
  const [propertySearchId, setPropertySearchId] = useState<string | null>(null);
  const [imageSource, setImageSource] = useState<'address' | 'upload' | 'webcam' | null>(null);

  return (
    <FrameContext.Provider
      value={{
        frameStyle,
        imageStyle,
        isOrientaAddress,
        isChapelHouse,
        address,
        addressLine1,
        city,
        state,
        zipCode,
        selectedStyle,
        clientSessionId,
        carouselImages,
        selectedFrame,
        selectedSize,
        propertyImage,
        styleImages,
        propertySearchId,
        imageSource,
        setFrameStyle,
        setImageStyle,
        setIsOrientaAddress,
        setIsChapelHouse,
        setAddress,
        setAddressLine1,
        setCity,
        setState,
        setZipCode,
        setSelectedStyle,
        setClientSessionId,
        setCarouselImages,
        setSelectedFrame,
        setSelectedSize,
        setPropertyImage,
        setStyleImages,
        setPropertySearchId,
        setImageSource,
      }}
    >
      {children}
    </FrameContext.Provider>
  );
};